import { useState } from "react";
import { useLiveData, useActionWithRefresh } from "../hooks/useLiveData";
import { useAuth } from "../components/AuthContext";
import {
  getAllVersions,
  createVersion,
  promoteVersion,
  rollbackToVersion,
  redeployVersion,
  deployVersion,
  getUnversionedCommits,
  getLatestByStage,
  addCommitsToVersion,
  removeCommitFromVersion,
  updateVersionNotes,
  linkFeatureToVersion,
  linkBugToVersion,
} from "../services/versions";
import { getAllFeatures } from "../services/features";
import { getAllBugs } from "../services/bugs";
import { syncCommitsFromCodeCommit, getAllCommits } from "../services/commits";
import { reconcilePipelineState } from "../services/reconciler";
import { completeFeatureInVersion } from "../services/features";
import { fixBugInVersion } from "../services/bugs";
import type { Version, Commit, FeatureRequest, BugReport, Stage, VersionStatus } from "../types";

const stageOrder: Stage[] = ["dev", "beta", "prod"];
const stageLabels: Record<Stage, string> = { dev: "Dev", beta: "Beta", prod: "Prod" };
const stageColors: Record<Stage, string> = { dev: "#3b82f6", beta: "#f59e0b", prod: "#22c55e" };

const statusIcons: Record<VersionStatus, string> = {
  created: "📦",
  queued: "⏳",
  deploying: "🔄",
  deployed: "✅",
  "awaiting-promotion": "⏫",
  "rolled-back": "⏪",
  failed: "❌",
};

export default function Versions() {
  const { can } = useAuth();
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCommits, setSelectedCommits] = useState<string[]>([]);
  const [newVersionName, setNewVersionName] = useState("");
  const [syncing, setSyncing] = useState(false);

  // Rollback/Redeploy modal state
  const [rollbackModal, setRollbackModal] = useState<{
    version: Version;
    mode: "rollback" | "redeploy";
  } | null>(null);
  const [rollbackStage, setRollbackStage] = useState<Stage>("dev");
  const [hotfixCommitId, setHotfixCommitId] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [showLinkPanel, setShowLinkPanel] = useState(false);

  // Live data — reconciler runs before fetching versions
  const versions = useLiveData<Version[]>({
    fetcher: async () => {
      await reconcilePipelineState();
      return getAllVersions();
    },
    interval: 8000,
  });
  const unversioned = useLiveData<Commit[]>({ fetcher: getUnversionedCommits, interval: 15000 });
  const allCommits = useLiveData<Commit[]>({ fetcher: getAllCommits, interval: 30000 });
  const features = useLiveData<FeatureRequest[]>({ fetcher: getAllFeatures, interval: 30000 });
  const bugs = useLiveData<BugReport[]>({ fetcher: getAllBugs, interval: 30000 });

  // Active versions per stage (live)
  const activeVersions = useLiveData<Record<Stage, Version | undefined>>({
    fetcher: async () => {
      const [dev, beta, prod] = await Promise.all([
        getLatestByStage("dev"),
        getLatestByStage("beta"),
        getLatestByStage("prod"),
      ]);
      return { dev, beta, prod };
    },
    interval: 8000,
  });

  const refreshAll = async () => {
    await Promise.all([versions.refresh(), unversioned.refresh(), activeVersions.refresh()]);
  };

  const { busy, actionError, execute, clearError } = useActionWithRefresh(refreshAll);

  async function handleSync() {
    setSyncing(true);
    try {
      await syncCommitsFromCodeCommit();
      await unversioned.refresh();
    } finally {
      setSyncing(false);
    }
  }

  async function handleCreateVersion(e: React.FormEvent) {
    e.preventDefault();
    if (!newVersionName || selectedCommits.length === 0) return;

    await createVersion({ version: newVersionName, commitIds: selectedCommits });
    setNewVersionName("");
    setSelectedCommits([]);
    setShowCreateForm(false);
    await refreshAll();
  }

  function toggleCommit(commitId: string) {
    setSelectedCommits((prev) =>
      prev.includes(commitId) ? prev.filter((id) => id !== commitId) : [...prev, commitId]
    );
  }

  function getProgressPercent(version: Version): number {
    const stageProgress: Record<Stage, number> = { dev: 33, beta: 66, prod: 100 };

    if (version.status === "deployed" || version.status === "awaiting-promotion") {
      return stageProgress[version.stage];
    }
    if (version.status === "deploying") {
      // Midway through current stage deploy
      const base = version.stage === "dev" ? 0 : version.stage === "beta" ? 33 : 66;
      return base + 15;
    }
    if (version.status === "queued") return 5;
    if (version.status === "created") return 0;
    if (version.status === "failed" || version.status === "rolled-back") return 0;
    return 0;
  }

  const allVersions = versions.data || [];
  const linkedFeatures = selectedVersion
    ? (features.data || []).filter((f) => selectedVersion.linkedFeatures.includes(f.id))
    : [];
  const linkedBugs = selectedVersion
    ? (bugs.data || []).filter((b) => selectedVersion.linkedBugs.includes(b.id))
    : [];

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2>Version Controller</h2>
            <p>Bundle commits into versions, deploy, promote, and rollback.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {versions.lastUpdated && (
              <span className="live-indicator">
                <span className="live-dot" />
                {versions.lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button className="btn" onClick={handleSync} disabled={syncing}>
              {syncing ? "⟳" : "↻"} Sync
            </button>
            <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
              + New Version
            </button>
          </div>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          ⚠️ {actionError}
          <button onClick={clearError} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {/* Active deployments per stage */}
      <div className="active-stages">
        {stageOrder.map((stage) => {
          const active = activeVersions.data?.[stage];
          return (
            <div key={stage} className="active-stage-card animate-fade-in">
              <div className="asc-bar" style={{ backgroundColor: stageColors[stage] }} />
              <div className="asc-content">
                <div className="asc-header">
                  <span className="asc-stage">{stageLabels[stage]}</span>
                  <span className="asc-version">{active ? `v${active.version}` : "No deploy"}</span>
                </div>
                {active && (
                  <div className="asc-meta">
                    <span>{active.commits.length} commits</span>
                    <span>·</span>
                    <span>{new Date(active.deployedAt || active.createdAt).toLocaleDateString()}</span>
                  </div>
                )}
                {active && (
                  <div className="asc-actions">
                    {stage !== "prod" && can("versions:promote-" + stageOrder[stageOrder.indexOf(stage) + 1] as any) && (
                      <button
                        className="mini-btn promote"
                        onClick={() => execute(`promote-${active.id}`, () => promoteVersion(active.id, stageOrder[stageOrder.indexOf(stage) + 1]))}
                        disabled={busy === `promote-${active.id}`}
                      >
                        {busy === `promote-${active.id}` ? "⟳" : "↗"} Promote
                      </button>
                    )}
                    {can("pipeline:rollback") && (
                      <button
                        className="mini-btn rollback"
                        onClick={() => setRollbackModal({ version: active!, mode: "rollback" })}
                      >
                        ⏪ Rollback
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create version form */}
      {showCreateForm && (
        <div className="card animate-slide-down" style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "1rem" }}>Create Version from Commits</h3>
          {(unversioned.data || []).length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>
              No unversioned commits. Click "Sync" to pull latest from CodeCommit.
            </p>
          ) : (
            <form onSubmit={handleCreateVersion}>
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.25rem" }}>Version (semver)</label>
                <input
                  type="text"
                  value={newVersionName}
                  onChange={(e) => setNewVersionName(e.target.value)}
                  placeholder="1.0.0"
                  required
                  style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)", width: "200px" }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                  Select commits ({selectedCommits.length} selected)
                </label>
                <div className="commit-list">
                  {(unversioned.data || []).map((c) => (
                    <label key={c.commitId} className={`commit-row ${selectedCommits.includes(c.commitId) ? "selected" : ""}`}>
                      <input
                        type="checkbox"
                        checked={selectedCommits.includes(c.commitId)}
                        onChange={() => toggleCommit(c.commitId)}
                      />
                      <code>{c.commitId.substring(0, 7)}</code>
                      <span className="commit-msg">{c.message.split("\n")[0]}</span>
                      <span className="commit-meta">{c.author} · {new Date(c.timestamp).toLocaleDateString()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={selectedCommits.length === 0 || !newVersionName}>
                Create v{newVersionName || "..."}
              </button>
              <button type="button" className="btn" style={{ marginLeft: "0.5rem" }} onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
            </form>
          )}
        </div>
      )}

      {/* Version history with progress bars */}
      <div className="card animate-fade-in" style={{ marginTop: "2rem" }}>
        <h3 style={{ marginBottom: "1rem" }}>All Versions</h3>
        <div className="version-list">
          {allVersions.map((v) => {
            const progress = getProgressPercent(v);
            const isActive = v.status === "deploying" || v.status === "queued";
            return (
              <div
                key={v.id}
                className={`version-card animate-fade-in ${selectedVersion?.id === v.id ? "selected" : ""} ${v.status === "rolled-back" ? "rolled-back" : ""}`}
                onClick={() => setSelectedVersion(v)}
              >
                {/* Progress bar */}
                <div className="vc-progress-track">
                  <div
                    className={`vc-progress-bar ${isActive ? "animating" : ""}`}
                    style={{
                      width: `${progress}%`,
                      backgroundColor: v.status === "failed" ? "var(--danger)" : v.status === "rolled-back" ? "var(--text-muted)" : stageColors[v.stage],
                    }}
                  />
                  {/* Stage markers */}
                  <div className="vc-progress-markers">
                    {stageOrder.map((s, i) => (
                      <div
                        key={s}
                        className={`vc-marker ${stageOrder.indexOf(v.stage) >= i && (v.status === "deployed" || v.status === "awaiting-promotion") ? "reached" : ""}`}
                        style={{ left: `${((i + 1) / 3) * 100}%` }}
                        title={stageLabels[s]}
                      />
                    ))}
                  </div>
                </div>

                <div className="vc-body">
                  <div className="vc-left">
                    <span className="vc-icon">{statusIcons[v.status]}</span>
                    <div>
                      <div className="vc-title">
                        <strong>v{v.version}</strong>
                        <span className={`badge badge-${v.stage === "prod" ? "success" : v.stage === "beta" ? "warning" : "info"}`}>
                          {stageLabels[v.stage]}
                        </span>
                        <span className={`badge badge-${v.status === "deployed" ? "success" : v.status === "awaiting-promotion" ? "warning" : v.status === "failed" ? "danger" : "warning"}`}>
                          {v.status === "awaiting-promotion" ? `awaiting ${stageOrder[stageOrder.indexOf(v.stage) + 1] ? stageLabels[stageOrder[stageOrder.indexOf(v.stage) + 1]] : ""}` : v.status}
                        </span>
                      </div>
                      <div className="vc-subtitle">
                        {v.commits.length} commit{v.commits.length !== 1 ? "s" : ""}
                        {v.linkedFeatures.length > 0 && ` · ✨${v.linkedFeatures.length}`}
                        {v.linkedBugs.length > 0 && ` · 🐛${v.linkedBugs.length}`}
                      </div>
                    </div>
                  </div>

                  <div className="vc-right">
                    <div className="vc-actions">
                      {v.status === "created" && can("pipeline:trigger") && (
                        <button
                          className="mini-btn deploy"
                          onClick={(e) => { e.stopPropagation(); execute(`deploy-${v.id}`, () => deployVersion(v.id)); }}
                          disabled={busy === `deploy-${v.id}`}
                        >
                          {busy === `deploy-${v.id}` ? "⟳" : "▶"} Deploy
                        </button>
                      )}
                      {v.status === "failed" && can("pipeline:trigger") && (
                        <button
                          className="mini-btn deploy"
                          onClick={(e) => { e.stopPropagation(); execute(`deploy-${v.id}`, () => deployVersion(v.id)); }}
                          disabled={busy === `deploy-${v.id}`}
                        >
                          ↻ Retry
                        </button>
                      )}
                      {(v.status === "deployed" || v.status === "awaiting-promotion") && can("pipeline:rollback") && (
                        <button
                          className="mini-btn rollback"
                          onClick={(e) => { e.stopPropagation(); setRollbackModal({ version: v, mode: "rollback" }); setRollbackStage(v.stage); }}
                        >
                          ⏪ Rollback
                        </button>
                      )}
                      {(v.status === "deployed" || v.status === "awaiting-promotion") && can("pipeline:trigger") && (
                        <button
                          className="mini-btn deploy"
                          onClick={(e) => { e.stopPropagation(); setRollbackModal({ version: v, mode: "redeploy" }); setRollbackStage(v.stage); }}
                        >
                          ↻ Redeploy
                        </button>
                      )}
                    </div>
                    <span className="vc-time">{new Date(v.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {allVersions.length === 0 && (
            <p style={{ padding: "1.5rem", color: "var(--text-muted)", textAlign: "center" }}>
              No versions yet. Sync commits and create your first version.
            </p>
          )}
        </div>
      </div>

      {/* Version detail panel */}
      {selectedVersion && (
        <div className="card animate-slide-up" style={{ marginTop: "1.5rem" }}>
          <h3>v{selectedVersion.version}
            <span className={`badge badge-${selectedVersion.stage === "prod" ? "success" : selectedVersion.stage === "beta" ? "warning" : "info"}`} style={{ marginLeft: "0.75rem" }}>
              {stageLabels[selectedVersion.stage]}
            </span>
            <span className={`badge badge-${selectedVersion.status === "deployed" ? "success" : selectedVersion.status === "failed" ? "danger" : "warning"}`} style={{ marginLeft: "0.5rem" }}>
              {selectedVersion.status}
            </span>
          </h3>

          {/* Editable commit list for "created" or "failed" versions */}
          {(selectedVersion.status === "created" || selectedVersion.status === "failed") && (
            <div style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <h4 style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
                  📝 Commits ({selectedVersion.commits.length})
                </h4>
                <span style={{ fontSize: "0.75rem", color: "var(--primary)" }}>Editable — add or remove commits before deploying</span>
              </div>

              {/* Current commits with remove button */}
              <div className="commit-list" style={{ marginBottom: "0.75rem" }}>
                {(allCommits.data || [])
                  .filter((c) => selectedVersion.commits.includes(c.commitId))
                  .map((c) => (
                    <div key={c.commitId} className="commit-row selected">
                      <code>{c.commitId.substring(0, 7)}</code>
                      <span className="commit-msg">{c.message.split("\n")[0]}</span>
                      <button
                        className="mini-btn rollback"
                        style={{ marginLeft: "auto" }}
                        onClick={() => execute(`rm-commit-${c.commitId}`, async () => {
                          await removeCommitFromVersion(selectedVersion.id, c.commitId);
                          await refreshAll();
                          // Refresh selected version
                          const updated = (versions.data || []).find((v) => v.id === selectedVersion.id);
                          if (updated) setSelectedVersion(updated);
                        })}
                        disabled={selectedVersion.commits.length <= 1 || busy !== null}
                        title={selectedVersion.commits.length <= 1 ? "Can't remove last commit" : "Remove from version"}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
              </div>

              {/* Add unversioned commits */}
              {(unversioned.data || []).length > 0 && (
                <div>
                  <h5 style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.35rem" }}>
                    Add available commits:
                  </h5>
                  <div className="commit-list" style={{ maxHeight: "150px" }}>
                    {(unversioned.data || []).map((c) => (
                      <div key={c.commitId} className="commit-row">
                        <code>{c.commitId.substring(0, 7)}</code>
                        <span className="commit-msg">{c.message.split("\n")[0]}</span>
                        <button
                          className="mini-btn deploy"
                          style={{ marginLeft: "auto" }}
                          onClick={() => execute(`add-commit-${c.commitId}`, async () => {
                            await addCommitsToVersion(selectedVersion.id, [c.commitId]);
                            await refreshAll();
                          })}
                          disabled={busy !== null}
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedVersion.changelog && (
            <pre className="changelog-block animate-fade-in">
              {selectedVersion.changelog}
            </pre>
          )}

          {/* Manual release notes */}
          <div style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <h4 style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>📝 Release Notes</h4>
              {!editingNotes ? (
                <button
                  className="mini-btn deploy"
                  onClick={() => { setEditingNotes(true); setNotesText(selectedVersion.notes || ""); }}
                >
                  ✎ Edit
                </button>
              ) : (
                <div style={{ display: "flex", gap: "0.4rem" }}>
                  <button
                    className="mini-btn promote"
                    onClick={async () => {
                      await updateVersionNotes(selectedVersion.id, notesText);
                      setEditingNotes(false);
                      await refreshAll();
                    }}
                    disabled={busy !== null}
                  >
                    ✓ Save
                  </button>
                  <button
                    className="mini-btn rollback"
                    onClick={() => setEditingNotes(false)}
                  >
                    ✕ Cancel
                  </button>
                </div>
              )}
            </div>
            {editingNotes ? (
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Add release notes — describe what's in this version, known issues, deployment instructions..."
                rows={5}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid var(--border)",
                  resize: "vertical",
                  fontFamily: "inherit",
                  fontSize: "0.85rem",
                }}
              />
            ) : selectedVersion.notes ? (
              <div style={{ background: "var(--bg)", padding: "0.75rem 1rem", borderRadius: "6px", fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                {selectedVersion.notes}
              </div>
            ) : (
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                No release notes. Click Edit to add context about this version.
              </p>
            )}
          </div>

          <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <h4 style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
                  ✨ Features ({linkedFeatures.length})
                </h4>
              </div>
              {linkedFeatures.length === 0 ? (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No features linked.</p>
              ) : (
                linkedFeatures.map((f) => (
                  <div key={f.id} className="linked-item animate-fade-in">
                    <span className="badge badge-success">✓</span>
                    <span style={{ marginLeft: "0.5rem" }}>{f.title}</span>
                  </div>
                ))
              )}
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <h4 style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
                  🐛 Bugs Fixed ({linkedBugs.length})
                </h4>
              </div>
              {linkedBugs.length === 0 ? (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No bugs linked.</p>
              ) : (
                linkedBugs.map((b) => (
                  <div key={b.id} className="linked-item animate-fade-in">
                    <span className="badge badge-success">✓</span>
                    <span style={{ marginLeft: "0.5rem" }}>{b.title}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Link items button */}
          <div style={{ marginTop: "1rem" }}>
            <button className="btn" onClick={() => setShowLinkPanel(!showLinkPanel)}>
              {showLinkPanel ? "Hide" : "+ Link Features / Bugs"}
            </button>
          </div>

          {/* Link panel */}
          {showLinkPanel && (
            <div className="animate-slide-down" style={{ marginTop: "1rem", padding: "1rem", background: "var(--bg)", borderRadius: "8px" }}>
              <h4 style={{ marginBottom: "0.75rem", fontSize: "0.9rem" }}>Link to v{selectedVersion.version}</h4>

              {/* Unlinked features */}
              {(() => {
                const unlinkable = (features.data || []).filter(
                  (f) => !f.completedInVersion && f.status !== "completed" && !selectedVersion.linkedFeatures.includes(f.id)
                );
                return unlinkable.length > 0 ? (
                  <div style={{ marginBottom: "1rem" }}>
                    <h5 style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.4rem" }}>Features</h5>
                    {unlinkable.map((f) => (
                      <div key={f.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                        <button
                          className="mini-btn promote"
                          onClick={() => execute(`link-feat-${f.id}`, async () => {
                            await completeFeatureInVersion(f.id, selectedVersion.id);
                            await linkFeatureToVersion(selectedVersion.id, f.id);
                            await refreshAll();
                          })}
                          disabled={busy !== null}
                        >
                          ✓ Link
                        </button>
                        <span style={{ fontSize: "0.85rem" }}>{f.title}</span>
                        <span className={`badge badge-${f.priority === "high" || f.priority === "critical" ? "danger" : "warning"}`} style={{ fontSize: "0.6rem" }}>{f.priority}</span>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {/* Unlinked bugs */}
              {(() => {
                const unlinkable = (bugs.data || []).filter(
                  (b) => !b.fixedInVersion && b.status !== "completed" && !selectedVersion.linkedBugs.includes(b.id)
                );
                return unlinkable.length > 0 ? (
                  <div>
                    <h5 style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.4rem" }}>Bug Reports</h5>
                    {unlinkable.map((b) => (
                      <div key={b.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                        <button
                          className="mini-btn promote"
                          onClick={() => execute(`link-bug-${b.id}`, async () => {
                            await fixBugInVersion(b.id, selectedVersion.id);
                            await linkBugToVersion(selectedVersion.id, b.id);
                            await refreshAll();
                          })}
                          disabled={busy !== null}
                        >
                          ✓ Link
                        </button>
                        <span style={{ fontSize: "0.85rem" }}>{b.title}</span>
                        <span className={`badge badge-${b.severity === "high" || b.severity === "critical" ? "danger" : "warning"}`} style={{ fontSize: "0.6rem" }}>{b.severity}</span>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {(() => {
                const noFeatures = (features.data || []).filter(
                  (f) => !f.completedInVersion && f.status !== "completed" && !selectedVersion.linkedFeatures.includes(f.id)
                ).length === 0;
                const noBugs = (bugs.data || []).filter(
                  (b) => !b.fixedInVersion && b.status !== "completed" && !selectedVersion.linkedBugs.includes(b.id)
                ).length === 0;
                return noFeatures && noBugs ? (
                  <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>All open items are already linked or completed.</p>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}
      {/* Rollback / Redeploy Modal */}
      {rollbackModal && (
        <div className="modal-overlay animate-fade-in" onClick={() => setRollbackModal(null)}>
          <div className="modal-content animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <h3>{rollbackModal.mode === "rollback" ? "⏪ Rollback" : "↻ Redeploy"} v{rollbackModal.version.version}</h3>

            <div className="modal-body">
              {/* Stage selector */}
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label>Target Stage</label>
                <div className="stage-selector">
                  {stageOrder.map((s) => (
                    <button
                      key={s}
                      className={`stage-option ${rollbackStage === s ? "active" : ""}`}
                      style={{ borderColor: rollbackStage === s ? stageColors[s] : "var(--border)" }}
                      onClick={() => setRollbackStage(s)}
                      type="button"
                    >
                      {stageLabels[s]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hotfix commit (redeploy mode only) */}
              {rollbackModal.mode === "redeploy" && (
                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label>Attach a hotfix commit (optional)</label>
                  <select
                    value={hotfixCommitId}
                    onChange={(e) => setHotfixCommitId(e.target.value)}
                    style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--border)" }}
                  >
                    <option value="">No additional commit — redeploy as-is</option>
                    {(allCommits.data || []).slice(0, 20).map((c) => (
                      <option key={c.commitId} value={c.commitId}>
                        {c.commitId.substring(0, 7)} — {c.message.split("\n")[0].substring(0, 60)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* What will happen */}
              <div className="modal-info" style={{ marginTop: "1rem" }}>
                {rollbackModal.mode === "rollback" ? (
                  <p>
                    This will mark <strong>v{rollbackModal.version.version}</strong> as rolled back at <strong>{stageLabels[rollbackStage]}</strong> and
                    redeploy the previous version's commit through the pipeline.
                  </p>
                ) : (
                  <p>
                    This will trigger a new pipeline execution deploying <strong>v{rollbackModal.version.version}</strong> to <strong>{stageLabels[rollbackStage]}</strong>
                    {hotfixCommitId ? ` with hotfix commit ${hotfixCommitId.substring(0, 7)} attached` : ""}.
                  </p>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className={`btn ${rollbackModal.mode === "rollback" ? "btn-danger" : "btn-primary"}`}
                onClick={() => {
                  if (rollbackModal.mode === "rollback") {
                    execute(`rollback-${rollbackModal.version.id}`, () =>
                      rollbackToVersion(rollbackModal.version.id, rollbackStage)
                    );
                  } else {
                    execute(`redeploy-${rollbackModal.version.id}`, () =>
                      redeployVersion(rollbackModal.version.id, rollbackStage, hotfixCommitId || undefined)
                    );
                  }
                  setRollbackModal(null);
                  setHotfixCommitId("");
                }}
                disabled={busy !== null}
              >
                {rollbackModal.mode === "rollback" ? "⏪ Confirm Rollback" : "↻ Redeploy Now"}
              </button>
              <button className="btn" onClick={() => { setRollbackModal(null); setHotfixCommitId(""); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
